import React, { createContext, useContext, useState, useEffect, lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { translations } from './data/content';

// ── Components ───────────────────────────────────────────────────
import Header from './components/Header';
import Hero from './components/Hero';

const About = lazy(() => import('./components/About'));
const Services = lazy(() => import('./components/Services'));
const Hosting = lazy(() => import('./components/Hosting'));
const Development = lazy(() => import('./components/Development'));
const Domain = lazy(() => import('./components/Domain'));
const FAQ = lazy(() => import('./components/FAQ'));
const Footer = lazy(() => import('./components/Footer'));

// ── i18n Context ─────────────────────────────────────────────────
export const I18nContext = createContext(null);
export function useI18n() { return useContext(I18nContext); }

// ── Loader ───────────────────────────────────────────────────────
function Loader() {
  return (
    <div className="flex items-center justify-center py-28" aria-hidden="true">
      <div className="flex gap-2">
        {[0, 1, 2].map(i => (
          <span key={i} className="block w-2 h-2 rounded-full"
            style={{ 
              background: 'var(--accent-cyan)', 
              opacity: 0.6,
              animation: `pulseDot 1.2s ease ${i * 0.2}s infinite` 
            }} />
        ))}
      </div>
    </div>
  );
}

// ── App ──────────────────────────────────────────────────────────
export default function App() {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('sityx_lang') || 'ar'; }
    catch { return 'ar'; }
  });

  const t = translations[lang];
  const isRTL = lang === 'ar';

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.lang = lang;
    html.dir = isRTL ? 'rtl' : 'ltr';
    body.classList.toggle('dir-rtl', isRTL);
    try { localStorage.setItem('sityx_lang', lang); } catch {}
  }, [lang, isRTL]);

  function toggleLang() { setLang(p => p === 'en' ? 'ar' : 'en'); }

  return (
    <HelmetProvider>
      <I18nContext.Provider value={{ lang, t, isRTL, toggleLang }}>
        <div className="noise-overlay" aria-hidden="true" />

        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-white focus:text-black focus:font-bold"
        >
          Skip to main content
        </a>

        <Header />

        <main id="main-content" role="main">
          <Hero />
          <Suspense fallback={<Loader />}><About /></Suspense>
          <Suspense fallback={<Loader />}><Services /></Suspense>
          <Suspense fallback={<Loader />}><Hosting /></Suspense>
          <Suspense fallback={<Loader />}><Development /></Suspense>
          <Suspense fallback={<Loader />}><Domain /></Suspense>
          <Suspense fallback={<Loader />}><FAQ /></Suspense>
          <Suspense fallback={<Loader />}><Footer /></Suspense>
        </main>
      </I18nContext.Provider>
    </HelmetProvider>
  );
}